"use client";

import { helpCenterUrl, helpSpaceUrl } from "@/lib/help-center-seo";
import type { PublicHelpSpace } from "@v1/backend/logic/pages/public_help_space_groups";
import { IconOrEmoji } from "@v1/ui/icon-or-emoji";
import { cn } from "@v1/ui/utils";
import { ArrowLeft, BookOpen } from "lucide-react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import type { ReactElement } from "react";
import { HelpBreadcrumb } from "./help-breadcrumb";
import { HelpChildPageList } from "./help-child-page-list";
import { HelpJsonLd } from "./help-json-ld";
import { useHelpHref } from "./help-link-base";
import { HelpSpaceSectionCard } from "./help-space-section-card";
import { buildHelpSpaceTree } from "./help-tree";

/** Space landing page: leaf level-2 pages as list, parents as cards. Sidebar order. */
export function HelpSpaceOverview({
  org_slug,
  space,
  includeJsonLd,
}: {
  readonly org_slug: string;
  readonly space: PublicHelpSpace;
  readonly includeJsonLd: boolean;
}): ReactElement {
  const t = useTranslations("public.helpSpaceOverview");
  const helpHref = useHelpHref(org_slug);
  const roots = buildHelpSpaceTree(space.pages);
  const articles = roots.filter((node) => node.children.length === 0);
  const sections = roots.filter((node) => node.children.length > 0);

  return (
    <div className="mx-auto w-full max-w-[936px]">
      {includeJsonLd ? (
        <HelpJsonLd
          data={{
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: t("alleThemen"),
                item: helpCenterUrl(org_slug),
              },
              {
                "@type": "ListItem",
                position: 2,
                name: space.name,
                item: helpSpaceUrl(org_slug, space.space_id),
              },
            ],
          }}
        />
      ) : null}
      <HelpBreadcrumb org_slug={org_slug} items={[]} current={space.name} />

      <header>
        <span
          aria-hidden
          className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl border border-border/60 bg-muted/50 text-primary/70"
        >
          {space.icon ? (
            <IconOrEmoji value={space.icon} fallback="" size={22} />
          ) : (
            <BookOpen className="h-[22px] w-[22px]" strokeWidth={1.75} />
          )}
        </span>
        <h1 className="text-[30px] font-bold leading-tight tracking-tight text-primary">
          {space.name}
        </h1>
        {space.description ? (
          <p className="mt-2 max-w-[60ch] text-[15px] leading-relaxed text-primary/60">
            {space.description}
          </p>
        ) : null}
      </header>

      {articles.length > 0 ? (
        <section className="mt-8">
          {/* Label sits on the list so the count reads as its heading. */}
          <h2 className="mb-2.5 text-[13px] font-semibold text-primary/70">
            {t("seiten", { count: articles.length })}
          </h2>
          <HelpChildPageList
            org_slug={org_slug}
            pages={articles.map((node) => ({
              page_id: node.page.page_id,
              title: node.page.title,
              child_count: 0,
            }))}
          />
        </section>
      ) : null}

      {sections.length > 0 ? (
        <div
          className={cn(
            "grid gap-4",
            articles.length > 0 ? "mt-8" : "mt-10",
            sections.length > 1 && "sm:grid-cols-2",
          )}
        >
          {sections.map((node) => (
            <HelpSpaceSectionCard
              key={node.page.page_id}
              org_slug={org_slug}
              node={node}
            />
          ))}
        </div>
      ) : null}

      <footer className="mt-10 border-t border-border/50 pt-4">
        <Link
          href={helpHref({ kind: "center" })}
          prefetch={false}
          className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-primary/60 transition-colors hover:text-primary"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          {t("alleThemen")}
        </Link>
      </footer>
    </div>
  );
}
